/**
 * Is any tab of this browser uploading photos right now?
 *
 * The payment guard answers 409 UPLOADS_PENDING while the server still expects
 * photos for the order. After a reload mid-upload that is never true: the page
 * that was sending them is gone, and the photos it had not sent were either
 * picked up again by the reload notice -- re-uploaded under new names -- or let
 * go. The server cannot tell, and waits a day before giving up on them. Seen
 * on stage: 320 photos in the cart, all uploaded, and the delivery step said
 * "some photos are still uploading" and offered to pay without them.
 *
 * So each upload holds a shared Web Lock while it runs, and the delivery step
 * asks whether anyone holds it. The browser drops the lock with the page, so a
 * closed, reloaded or killed tab never counts as uploading.
 */
const UPLOADING_LOCK = "foto-uploading";

type LockManagerLike = {
  request(
    name: string,
    options: { mode: "shared" },
    callback: () => Promise<unknown>,
  ): Promise<unknown>;
  query(): Promise<{ held?: { name?: string }[]; pending?: { name?: string }[] }>;
};

function lockManager(): LockManagerLike | null {
  try {
    if (typeof navigator === "undefined") return null;
    return (navigator as unknown as { locks?: LockManagerLike }).locks ?? null;
  } catch {
    return null;
  }
}

/** Mark this tab as uploading until the returned release is called. */
export function holdUploadActivity(): () => void {
  const locks = lockManager();
  if (!locks) return () => {};
  let release: () => void = () => {};
  const held = new Promise<void>((resolve) => {
    release = resolve;
  });
  locks.request(UPLOADING_LOCK, { mode: "shared" }, () => held).catch(() => {});
  return release;
}

/**
 * True while a tab of this browser uploads, false when none does, null when
 * the browser cannot say (no Web Locks): callers keep the old behaviour then.
 */
export async function uploadsActiveHere(): Promise<boolean | null> {
  const locks = lockManager();
  if (!locks) return null;
  try {
    const { held = [], pending = [] } = await locks.query();
    return [...held, ...pending].some((l) => l.name === UPLOADING_LOCK);
  } catch {
    return null;
  }
}
